import { LeftOutlined, SearchOutlined } from '@ant-design/icons';
import {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { CenterFlex, Flex, ScrollVFlex, VFlex } from '@/components/base';
import FadeDiv from '@/components/Common/FadeDiv';
import Spinner from '@/components/Common/Spinner';
import DataTableOptionItem, {
  type OptionItemProps,
} from '@/components/DataTable/Dropdown/DataTableOptionItem';
import Input from '@/components/Inputs/Input';
import SelectPortal from '@/components/Select/SelectPortal';
import Tree from '@/components/Select/Tree';
import treeSelectUtils from '@/components/Select/treeSelectUtils';
import type { ModalOptions, TreeSelectItemBase } from '@/components/Select/types';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useDropdownPos from '@/hooks/pure/useDropdownPos';
import { usePopup } from '@/stores/pure/PopupStore';
import filterUtils from '@/utils/filter';
import { lowerCaseMatch } from '@/utils/util';

type Props = {
  filterOption: FilterOptionT | null;
  currFilterQueryFormat: FilterQueryFormatT;
  onChange?: (filterQueryFormat: FilterQueryFormatT) => void;
  onSelect?: (filterValue: FilterKeyValue) => void;
  style?: CSSProperties;
  needDefault?: boolean;
  status: boolean;
  close: () => void;
  triggerEl: HTMLDivElement | null;
  modalOptions?: ModalOptions;
  title?: string;
  isChildSelect?: boolean;
  onBackClick?: () => void;
};

type ChildFilterOption = {
  filterOption: FilterOptionT | null;
  title: string;
};

type States = {
  dataList: PaginatedData<FilterOptionItemT>[];
  page: number;
  hasNextPage: boolean;
  searchValue: string;
  loading: boolean;
  nextPageLoading: boolean;
};

const FilterItemSelect = (props: Props) => {
  const { dropdownPos, setDropdownRef } = useDropdownPos(props.status, {
    el: props.triggerEl,
    options: props.modalOptions,
  });

  return (
    <SelectPortal close={props.close} style={dropdownPos} status={props.status}>
      <Dropdown {...props} ref={setDropdownRef} />
    </SelectPortal>
  );
};

const Dropdown = forwardRef(
  (props: Props, ref: ForwardedRef<HTMLDivElement | null>) => {
    const color = useColor();
    const asyncSearch = useMemo(() => {
      if (props.filterOption?.asyncData) {
        if (typeof props.filterOption?.asyncData.asyncSearch === 'undefined') {
          return true;
        }
        return props.filterOption?.asyncData.asyncSearch;
      }
      return false;
    }, [props.filterOption?.asyncData]);
    const [state, setState] = useState<States>({
      dataList: [],
      page: 0,
      hasNextPage: false,
      searchValue: '',
      loading: false,
      nextPageLoading: false,
    });

    const [childListState, setChildListState] =
      useState<ChildFilterOption | null>(null);

    const selectFilter = (filterValue: FilterKeyValue) => {
      props.onSelect?.(filterValue);
      const newQueryFormat = {
        ...props.currFilterQueryFormat,
      };
      let newStatus = [...props.currFilterQueryFormat.filterValues];

      const targetIndex = newStatus.findIndex(
        (status) => status?.filterKey === filterValue.filterKey,
      );

      if (targetIndex === -1) {
        if (props.filterOption?.singleSelect) {
          newStatus = [filterValue];
          newQueryFormat.filterValues = newStatus;
          props.onChange?.(newQueryFormat);
          return;
        }

        newStatus.push(filterValue);
        newQueryFormat.filterValues = newStatus;
        props.onChange?.(newQueryFormat);
        return;
      } else {
        if (props.needDefault) {
          newStatus = [filterValue];
          newQueryFormat.filterValues = newStatus;
          props.onChange?.(newQueryFormat);
          return;
        }
        newStatus.splice(targetIndex, 1);
        newQueryFormat.filterValues = newStatus;
        props.onChange?.(newQueryFormat);
        return;
      }
    };

    const fetchData = useCallback(
      async (page?: number, acc?: boolean, searchValue?: string) => {
        if (!props.filterOption?.asyncData?.fetchList) return;
        setState((prev) => ({
          ...prev,
          loading: true,
        }));

        const res = await props.filterOption?.asyncData.fetchList(
          state.dataList,
          state.page,
          page,
          searchValue,
          acc,
        );
        if (!res) return;

        setState((prev) => ({
          ...prev,
          page: res.page || 0,
          hasNextPage: res.hasNextPage,
          dataList: acc ? prev.dataList.concat(res.list) : res.list,
          loading: false,
        }));
      },
      [props.filterOption?.asyncData, state.dataList, state.page],
    );

    useEffect(() => {
      fetchData(0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dataList = useMemo(() => {
      if (props.filterOption?.asyncData) {
        return state.dataList;
      } else {
        if (!props.filterOption?.filterList) return [];
        return props.filterOption?.filterList;
      }
    }, [
      props.filterOption?.asyncData,
      props.filterOption?.filterList,
      state.dataList,
    ]);

    const filteredDataList = useMemo(() => {
      if (asyncSearch) {
        return dataList;
      }
      return dataList.filter((option) =>
        lowerCaseMatch(option?.filterName, state.searchValue),
      );
    }, [asyncSearch, dataList, state.searchValue]);

    const debounce = useDebouncedCallback(async (value: string) => {
      if (asyncSearch) {
        fetchData(0, false, value);
      }
    }, 400);

    const treeData = useMemo(() => {
      return mapTreeNode(dataList, state.searchValue);
    }, [dataList, state.searchValue]);

    const onCheck = ({
      value,
      exceptSelf,
      action,
    }: {
      value: StringNumberValue;
      exceptSelf?: boolean;
      action?: 'add' | 'remove';
    }) => {
      const target = treeSelectUtils.findNode(treeData, value);
      if (!target) return props.currFilterQueryFormat.filterValues;

      const childs = treeSelectUtils
        .getFlattenChildNodes(target.childNodes || [])
        .filter((data) => !data.checkOption?.disable);

      const newValues = [...props.currFilterQueryFormat.filterValues];

      const toggleItem = (
        item: TreeSelectItemBase,
        action?: 'add' | 'remove',
        exceptSelf?: boolean,
        // action이 지정되지 않은 경우 toggle을 기본동작으로 실행함
      ) => {
        const valueIndex = newValues.findIndex(
          (value) => value.filterKey === item.alias,
        );
        if (item.checkOption?.disable) return valueIndex;
        if (exceptSelf) return valueIndex;
        const newValue = {
          filterKey: item.alias,
          filterName: item.searchValue,
        };
        const condition = (el: FilterKeyValue) =>
          el.filterKey === newValue.filterKey;

        switch (action) {
          case 'add': {
            newValues.upsert(newValue, condition);
            break;
          }

          case 'remove': {
            newValues.remove(newValue, condition);
            break;
          }

          default: {
            newValues.toggle(newValue, condition);
            break;
          }
        }
        return valueIndex;
      };

      const targetIndex = toggleItem(target, action, exceptSelf);

      for (const t of childs) {
        if (action) {
          toggleItem(t, action);
        } else {
          if (targetIndex === -1) {
            toggleItem(t, 'remove');
            toggleItem(t, 'add');
          } else {
            toggleItem(t, 'remove');
          }
        }
      }

      props.onChange?.({
        ...props.currFilterQueryFormat,
        filterValues: newValues,
      });
    };

    if (childListState)
      return (
        <Dropdown
          {...props}
          isChildSelect
          onBackClick={() => setChildListState(null)}
          title={childListState.title}
          filterOption={childListState.filterOption}
        />
      );

    return (
      <Wrapper style={props.style} ref={ref} g={8}>
        {props.isChildSelect && (
          <Flex a="center" g={4} height="32px">
            <CenterFlex width="32px" height="32px" style={{ fontSize: 16 }}>
              <LeftOutlined onClick={props.onBackClick} />
            </CenterFlex>
            <Typo>{props.title}</Typo>
          </Flex>
        )}
        {props.filterOption?.searchable && (
          <Input
            style={{ height: 32 }}
            placeholder="Search..."
            name="searchValue"
            sizeType="m"
            leftIcon={<SearchOutlined />}
            value={state.searchValue}
            onChange={(e) => {
              debounce(e.currentTarget.value);
              setState((prev) => ({
                ...prev,
                searchValue: e.target.value,
              }));
            }}
          />
        )}
        {props.filterOption?.isTreeType ? (
          <ScrollVFlex
            f={1}
            style={{
              maxHeight: 400,
            }}
          >
            <Tree
              dataList={treeData}
              selectedList={props.currFilterQueryFormat.filterValues.map(
                (v) => {
                  return v.filterKey;
                },
              )}
              onCheck={onCheck}
            />
          </ScrollVFlex>
        ) : (
          <ScrollVFlex
            f={1}
            style={{
              maxHeight: 400,
            }}
          >
            <VFlex g="2px">
              {filteredDataList.map((option) => {
                if (!option) return null;
                return (
                  <DataTableOptionItemContainer
                    key={option?.filterKey}
                    currFilterQueryFormat={props.currFilterQueryFormat}
                    filterOption={props.filterOption}
                    onChange={props.onChange}
                    optionKey={{
                      filterKey: option.filterKey,
                      filterName: option.filterName,
                      paths: option.paths,
                    }}
                    name={option.filterOptionNode || option.filterName}
                    isChildSelected={filterUtils.findSelectedChild(
                      props.currFilterQueryFormat,
                      option,
                    )}
                    isSelected={
                      !!props.currFilterQueryFormat.filterValues.find(
                        (filter) => filter?.filterKey === option?.filterKey,
                      )
                    }
                    onCheck={selectFilter}
                    childFilterList={option.filterList}
                    setChildListState={setChildListState}
                    option={option}
                  />
                );
              })}
              {dataList.length === 0 && (
                <Flex style={{ height: 80 }} a="center" j="center">
                  <EmptyMessage>No contents</EmptyMessage>
                </Flex>
              )}
              {state.loading && (
                <FadeDiv status={true}>
                  <CenterFlex style={{ height: 48 }}>
                    <Spinner />
                  </CenterFlex>
                </FadeDiv>
              )}
              {!state.loading && state.hasNextPage && (
                <CenterFlex
                  onClick={() => fetchData(state.page + 1, true)}
                  style={{ height: 48, cursor: 'pointer' }}
                >
                  <Typo type="label3" color={color.gray500}>
                    Load more
                  </Typo>
                </CenterFlex>
              )}
            </VFlex>
          </ScrollVFlex>
        )}
      </Wrapper>
    );
  },
);

const mapTreeNode = (
  filterList?: FilterOptionItemT[],
  searchValue?: string,
) => {
  const mapped = mapChildNode(filterList);
  if (searchValue && searchValue.trim() !== '') {
    const filtered = treeSelectUtils.getFlattenChildNodes(mapped, searchValue);
    return filtered;
  }
  return mapped;
};

const mapChildNode = (
  filterList?: FilterOptionItemT[],
): TreeSelectItemBase[] => {
  const asyncGetChildNodes = async (filterOption: FilterOptionItemT) => {
    const res = await filterOption.asyncData?.fetchList([], 0);
    return (
      res?.list.map((item) => ({
        alias: item.filterKey,
        searchValue: item.filterName,
        node: item.filterOptionNode,
        childNodes: mapChildNode(item.filterList),
      })) || []
    );
  };

  return (
    filterList?.map((d: FilterOptionItemT) => {
      return {
        alias: d.filterKey,
        searchValue: d.filterName,
        checkOption: {
          disable: d.disableCheck,
        },
        node: d.filterOptionNode,
        childNodes: d.filterList ? mapChildNode(d.filterList) : undefined,
        asyncGetChildNodes: d.asyncData
          ? () => asyncGetChildNodes(d)
          : undefined,
      };
    }) || []
  );
};

const DataTableOptionItemContainer = (
  props: OptionItemProps<FilterKeyValue> & {
    filterOption: FilterOptionT | null;
    currFilterQueryFormat: FilterQueryFormatT;
    onChange?: (filterQueryFormat: FilterQueryFormatT) => void;
    option: FilterOptionItemT;
    setChildListState: (filterOption: ChildFilterOption) => void;
  },
) => {
  const setPopup = usePopup();

  const onValidConfirm = () => {
    props.onChange?.({
      columnKey: props.currFilterQueryFormat.columnKey,
      filterValues: [props.optionKey],
      operator: props.currFilterQueryFormat.operator,
    });
  };

  const onCheck = (filterStatus: FilterKeyValue) => {
    if (props.filterOption?.validate) {
      const isValid = props.filterOption.validate.filterValidator(
        props.currFilterQueryFormat.filterValues,
        filterStatus.filterKey as string,
      );
      if (!isValid) {
        setPopup({
          title: props.filterOption.validate.title,
          message: props.filterOption.validate.message,
          onConfirm: onValidConfirm,
          popupTheme: 'negative',
          type: 'confirm',
        });
        return;
      }
    }
    props.onCheck(props.optionKey);
  };

  const onCaretClick = async () => {
    props.setChildListState({
      filterOption: {
        ...props.option,
        columnName: '',
      },
      title: props.option.filterName || '',
    });
  };

  return (
    <DataTableOptionItem
      {...props}
      useDepth={props.option.useDepth}
      disableCheck={props.option.disableCheck}
      onCheck={onCheck}
      onCaretClick={onCaretClick}
    />
  );
};

const Wrapper = styled(VFlex)`
  overflow: auto;
  box-sizing: border-box;
  min-width: 200px;
  max-width: 600px;
  padding: 8px;
`;

const EmptyMessage = styled(Typo).attrs({
  type: 'label3',
})`
  color: ${(props) => props.theme.colors.gray500};
`;

export default FilterItemSelect;
